// src/pages/FraudRules.js
// Fraud Management → Fraud Rules (fraud_config/default) configuration editor.
// Read-only view of current values + editable form → Save button.
// Future-proof: unknown numeric fields are auto-rendered below the 4 known sections.

import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import DashboardNavbar from '../components/DashboardNavbar';
import useFraudConfig, { KNOWN_FIELDS } from '../hooks/useFraudConfig';

// ─── Shared input style (identical to Blacklist / FraudLogsFilter) ────────────
const inputStyle = {
  backgroundColor: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 8,
  fontSize: 15,
  fontWeight: 700,
  padding: '10px 14px',
  width: '100%',
  outline: 'none',
  transition: 'border-color 0.15s',
};

// ─── In-page toast (same pattern as Blacklist.js) ────────────────────────────
const Toast = ({ toast }) => {
  if (!toast) return null;
  const isError = toast.type === 'error';
  return (
    <div
      style={{
        position: 'fixed', bottom: 28, right: 28, zIndex: 2000,
        background: isError ? 'rgba(239,68,68,0.92)' : 'rgba(34,197,94,0.92)',
        backdropFilter: 'blur(8px)', color: '#fff',
        borderRadius: 10, padding: '12px 20px',
        fontSize: 13, fontWeight: 600,
        boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', gap: 10,
        maxWidth: 380, animation: 'fcToastIn 0.25s ease',
      }}
    >
      <span>{isError ? '✗' : '✓'}</span>
      <span>{toast.message}</span>
      <style>{`@keyframes fcToastIn{from{transform:translateY(16px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
    </div>
  );
};

// ─── Error banner ─────────────────────────────────────────────────────────────
const ErrorBanner = ({ message }) => (
  <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#EF4444' }}>
    ⚠️ {message}
  </div>
);

// ─── Skeleton number input ────────────────────────────────────────────────────
const SkeletonInput = () => (
  <div className="fraud-skeleton" style={{ width: '100%', height: 44, borderRadius: 8 }} />
);

// ─── Format date ──────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

// ─── Humanise camelCase key into a readable label ─────────────────────────────
const humanise = (key) =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();

// ─── Known config section card ────────────────────────────────────────────────
const KnownSectionCard = ({ field, value, savedValue, error, onChange, loading }) => {
  const isDirty = value !== String(savedValue ?? '');
  return (
    <div
      className="theme-card rounded-3 p-4 h-100"
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      {/* Accent bar — same as StatCard */}
      <div
        style={{
          position: 'absolute', top: 0, left: 0,
          width: 3, height: '100%',
          background: field.accent, borderRadius: '4px 0 0 4px',
        }}
      />

      {/* Icon bubble */}
      <div
        style={{
          position: 'absolute', top: 16, right: 16,
          width: 40, height: 40, borderRadius: '50%',
          background: field.accentBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20,
        }}
      >
        {field.icon}
      </div>

      {/* Section title */}
      <div
        style={{
          fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
          letterSpacing: 1, color: field.accent, marginBottom: 4,
        }}
      >
        {field.section}
      </div>

      {/* Field label */}
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>
        {field.label}
        {isDirty && !loading && (
          <span
            style={{
              marginLeft: 8, fontSize: 10, color: '#F59E0B',
              background: 'rgba(245,158,11,0.12)',
              border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: 4, padding: '1px 6px',
            }}
          >
            unsaved
          </span>
        )}
      </div>

      {/* Description */}
      <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.6 }}>
        {field.description}
      </p>

      {/* Input */}
      {loading ? (
        <SkeletonInput />
      ) : (
        <>
          <input
            id={`fc-input-${field.key}`}
            type="number"
            min="1"
            step="1"
            value={value}
            onChange={(e) => onChange(field.key, e.target.value)}
            placeholder="Enter value…"
            style={{
              ...inputStyle,
              borderColor: error
                ? '#EF4444'
                : isDirty
                ? `${field.accent}88`
                : 'rgba(255,255,255,0.15)',
              color: isDirty ? field.accent : 'var(--text-primary)',
            }}
            onFocus={(e) => (e.target.style.borderColor = `${field.accent}`)}
            onBlur={(e) =>
              (e.target.style.borderColor = error
                ? '#EF4444'
                : isDirty
                ? `${field.accent}88`
                : 'rgba(255,255,255,0.15)')
            }
          />
          {error && (
            <small style={{ color: '#EF4444', fontSize: 11, marginTop: 4, display: 'block' }}>
              {error}
            </small>
          )}
          {savedValue != null && (
            <small style={{ color: 'var(--text-secondary)', fontSize: 10, marginTop: 6, display: 'block' }}>
              Current saved: <strong style={{ color: 'var(--text-primary)' }}>{savedValue}</strong>
            </small>
          )}
        </>
      )}
    </div>
  );
};

// ─── Unknown field row (inside the Additional Config card) ────────────────────
const UnknownFieldRow = ({ fieldKey, value, savedValue, error, onChange, loading }) => {
  const isDirty = value !== String(savedValue ?? '');
  return (
    <div style={{ borderBottom: '1px solid var(--divider)', paddingBottom: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
          {humanise(fieldKey)}
        </span>
        <code style={{ fontSize: 10, color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.05)', padding: '1px 6px', borderRadius: 3 }}>
          {fieldKey}
        </code>
        {isDirty && !loading && (
          <span style={{ fontSize: 10, color: '#F59E0B', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 4, padding: '1px 6px' }}>
            unsaved
          </span>
        )}
      </div>
      {loading ? (
        <SkeletonInput />
      ) : (
        <>
          <input
            id={`fc-input-${fieldKey}`}
            type="number"
            min="1"
            step="1"
            value={value}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            placeholder="Enter value…"
            style={{
              ...inputStyle,
              fontSize: 14,
              borderColor: error ? '#EF4444' : isDirty ? '#D9FF1F88' : 'rgba(255,255,255,0.15)',
              color: isDirty ? 'var(--accent-lime)' : 'var(--text-primary)',
            }}
          />
          {error && (
            <small style={{ color: '#EF4444', fontSize: 11, marginTop: 4, display: 'block' }}>{error}</small>
          )}
          {savedValue != null && (
            <small style={{ color: 'var(--text-secondary)', fontSize: 10, marginTop: 4, display: 'block' }}>
              Current saved: <strong style={{ color: 'var(--text-primary)' }}>{savedValue}</strong>
            </small>
          )}
        </>
      )}
    </div>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────
const FraudRules = () => {
  const cfg = useFraudConfig();

  return (
    <div className="d-flex entire-bg-color">
      <Sidebar />

      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <DashboardNavbar />
        <div style={{ marginTop: '70px' }} />

        <section className="py-4 nav-body-bg btm-rad">
          <Container fluid className="px-4">

            {/* ── Top bar ── */}
            <div
              className="theme-card rounded-3 p-4 mb-4"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}
            >
              <div>
                <h3 className="text-h2 mb-1">Fraud Rules</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {cfg.lastUpdated && (
                    <span
                      style={{
                        fontSize: 11, color: 'var(--text-secondary)',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid var(--divider)',
                        borderRadius: 20, padding: '2px 10px',
                      }}
                    >
                      Last saved: {fmtDate(cfg.lastUpdated)}
                    </span>
                  )}
                  {cfg.isDirty && !cfg.loading && (
                    <span
                      style={{
                        fontSize: 11, fontWeight: 700,
                        color: '#F59E0B',
                        background: 'rgba(245,158,11,0.10)',
                        border: '1px solid rgba(245,158,11,0.3)',
                        borderRadius: 20, padding: '2px 10px',
                      }}
                    >
                      Unsaved changes
                    </span>
                  )}
                </div>
              </div>

              {/* Refresh button */}
              <button
                id="fc-refresh-btn"
                onClick={cfg.refresh}
                disabled={cfg.loading || cfg.saving}
                title="Reload from Firestore"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 8,
                  color: 'var(--text-secondary)',
                  fontSize: 18,
                  width: 40, height: 40,
                  cursor: cfg.loading ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.15s',
                  flexShrink: 0,
                }}
              >
                {cfg.loading ? '…' : '↻'}
              </button>
            </div>

            {/* ── Error banner ── */}
            {cfg.error && <ErrorBanner message={cfg.error} />}

            {/* ── Known section cards ── */}
            <Row className="g-3 mb-4">
              {KNOWN_FIELDS.map((field) => (
                <Col key={field.key} xs={12} md={6} xl={3}>
                  <KnownSectionCard
                    field={field}
                    value={cfg.form[field.key] ?? ''}
                    savedValue={cfg.savedValues[field.key]}
                    error={cfg.fieldErrors[field.key]}
                    onChange={cfg.setField}
                    loading={cfg.loading}
                  />
                </Col>
              ))}
            </Row>

            {/* ── Unknown / additional fields ── */}
            {(cfg.loading || cfg.unknownKeys.length > 0) && (
              <div className="theme-card rounded-3 p-4 mb-4" style={{ position: 'relative', overflow: 'hidden' }}>
                {/* Accent bar */}
                <div style={{ position: 'absolute', top: 0, left: 0, width: 3, height: '100%', background: 'var(--accent-lime)', borderRadius: '4px 0 0 4px' }} />

                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--accent-lime)', marginBottom: 4 }}>
                    Additional Configuration
                  </div>
                  <small style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    These fields were detected automatically from the document and are editable.
                  </small>
                </div>

                {cfg.loading ? (
                  <div className="fraud-skeleton" style={{ width: '100%', height: 64, borderRadius: 8 }} />
                ) : (
                  cfg.unknownKeys.map((key) => (
                    <UnknownFieldRow
                      key={key}
                      fieldKey={key}
                      value={cfg.form[key] ?? ''}
                      savedValue={cfg.savedValues[key]}
                      error={cfg.fieldErrors[key]}
                      onChange={cfg.setField}
                      loading={cfg.loading}
                    />
                  ))
                )}
              </div>
            )}

            {/* ── Save button ── */}
            <div
              className="theme-card rounded-3 p-4"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}
            >
              <div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 600 }}>
                  Ready to save?
                </div>
                <small style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  All fields are saved together as a single document update.
                </small>
              </div>
              <button
                id="fc-save-btn"
                onClick={cfg.save}
                disabled={cfg.saving || cfg.loading}
                style={{
                  background: cfg.saving || cfg.loading ? 'rgba(217,255,31,0.3)' : 'var(--accent-lime)',
                  border: 'none',
                  borderRadius: 10,
                  color: '#050014',
                  fontSize: 14,
                  fontWeight: 800,
                  padding: '12px 36px',
                  cursor: cfg.saving || cfg.loading ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 8,
                  transition: 'all 0.15s',
                  letterSpacing: 0.3,
                  minWidth: 200,
                  justifyContent: 'center',
                }}
              >
                {cfg.saving ? (
                  <>
                    <span className="admin-loader-sm" style={{ borderTopColor: '#050014' }} />
                    Saving…
                  </>
                ) : (
                  <>
                    💾 Save Configuration
                  </>
                )}
              </button>
            </div>

            <div style={{ height: 40 }} />
          </Container>
        </section>
      </div>

      <Toast toast={cfg.toast} />
    </div>
  );
};

export default FraudRules;
